import { useMemo } from "react";

import { css } from "@compiled/react";

import ContentPlatformImage from "@/components/content-platform-image/src";
import { DEFAULT_LOCALE, useLocale } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";
import { AssetComponent } from "@/templates/agile-hub/types";

export type BodyAssetComponentProps = Omit<AssetComponent, "__typename">;

export const BodyAssetComponent = (data: BodyAssetComponentProps) => {
    // NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob

    const bodyAssetCss = css({
        "display": "flex",
        "flexWrap": "wrap",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "img": {
            maxWidth: "100%",
            height: "auto"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h3, h4": {
            marginBlockStart: "0"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            fontSize: "1rem !important",
            lineHeight: "1.5",
            marginBlockStart: "0"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "a": {
            "color": "#0052cc",
            "textDecoration": "underline",
            "cursor": "pointer",
            "&:hover": {
                color: "#0065ff",
                textDecoration: "none"
            }
        }
    });

    const layout = data.assetRelatedText ? "6-6" : "12";
    const localeCode = useLocale();
    const html = useMemo(() => {
        let initialHtml = data.assetRelatedText || "";

        // Hacky stuff: Need to wrap tables in a div to make them scrollable
        const tableRegex = /<table[^>]*>[\s\S]*?<\/table>/g;
        const tableMatch = initialHtml.match(tableRegex);
        if (tableMatch) {
            const tableHtml = tableMatch[0];
            const newTableHtml = `<div class="table-wrapper">${tableHtml}</div>`;
            initialHtml = initialHtml.replace(tableHtml, newTableHtml);
        }

        // Hacky stuff pt2: Need to fix relative localized links
        const relativeLinkRegex = /(?:\"https:\/\/\/)(?<urlSlug>.+?)\"/g;
        initialHtml = initialHtml.replace(
            relativeLinkRegex,
            (_match, _p1, _offset, _string, groups) => {
                return localeCode === DEFAULT_LOCALE
                    ? `"/${groups.urlSlug}"`
                    : `"/${localeCode}/${groups.urlSlug}"`;
            }
        );

        // handle case where link is relative and not localized
        const relativeUrlRegex = new RegExp(
            `href="\\/(${localeCode}\\/)?([^"]*)"`,
            "g"
        );
        initialHtml = initialHtml.replace(
            relativeUrlRegex,
            (_match, _p1, _p2) => {
                // If the locale code is already present, return the match as is
                if (_p1) return _match;
                // Otherwise, prepend the locale code to the URL
                return `href="/${localeCode}/${_p2}"`;
            }
        );

        // handle case where absolute WAC links are not localized
        const absolutePathRegex =
            /\"https:\/\/www\.atlassian\.com\/(?<urlSlug>.+?)\"/g;
        initialHtml = initialHtml.replace(
            absolutePathRegex,
            (_match, _p1, _offset, _string, groups) => {
                return localeCode === DEFAULT_LOCALE
                    ? `"/${groups.urlSlug}"`
                    : `"/${localeCode}/${groups.urlSlug}"`;
            }
        );

        // Hacky stuff pt3: replace &amp;nbsp; with &nbsp;
        initialHtml = initialHtml.replace(/&amp;nbsp;/g, "&nbsp;");

        return initialHtml;
    }, [data.assetRelatedText, localeCode]);

    return (
        <div css={bodyAssetCss}>
            <Row layout={layout} alignItems="top">
                <div>
                    <ContentPlatformImage
                        src={data.asset?.url as string}
                        alt={data.asset?.description ?? data.asset?.name ?? ""}
                        baseWidth={740}
                        contentType={data.asset?.contentType}
                    />
                </div>
                {data.assetRelatedText && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: html
                        }}
                    />
                )}
            </Row>
        </div>
    );
};
